import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Sparkles, CheckCircle2, Wrench } from "lucide-react";
import { APP_VERSION, APP_CHANGELOG } from "@/lib/constants";

const STORAGE_KEY = "trace_last_seen_version";
const POLL_INTERVAL = 60_000; // 60s
const IS_DEV = import.meta.env.DEV;

function changeIcon(text: string) {
  const lower = text.toLowerCase();
  if (lower.startsWith("fix"))
    return <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn" />;
  return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />;
}

/** Standalone changelog dialog — usable from anywhere (e.g. Settings page) */
export function ChangelogDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card mx-4 w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="border-b border-line-2 bg-panel-2 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft">
              <Sparkles className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-ink">What's new in TRACE</h2>
              <p className="text-sm text-ink-3">Latest updates and improvements</p>
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] space-y-6 overflow-y-auto px-6 py-5">
          {APP_CHANGELOG.map((entry) => (
            <div key={entry.version}>
              <div className="mb-3 flex items-center justify-between">
                <span className="inline-flex items-center rounded-full bg-accent-soft px-3 py-1 text-xs font-bold text-accent">
                  v{entry.version}
                </span>
                <span className="text-xs text-ink-3">{entry.date}</span>
              </div>
              <ul className="space-y-2">
                {entry.changes.map((change, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-ink-2">
                    {changeIcon(change)}
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-line-2 px-6 py-4">
          <button
            onClick={() => onOpenChange(false)}
            className="btn btn--accent w-full px-4 py-2.5 text-sm font-medium"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Version gate: detects new deployments and forces the user to reload.
 *
 * Two detection mechanisms:
 * 1. localStorage check — on mount, compares last-seen version with APP_VERSION
 *    (catches "user returns after a deploy")
 * 2. Polling /version.json — every 60s, compares deployed version with in-memory
 *    APP_VERSION (catches "deploy while user has the app open")
 *
 * When a mismatch is detected the user sees a non-dismissible overlay with the
 * changelog and a reload button. No X, no escape — the only action is "Aggiorna".
 */
export function UpdateBanner() {
  const [needsReload, setNeedsReload] = useState(false);
  const [source, setSource] = useState<"localStorage" | "deploy">("localStorage");

  // 1. localStorage check — first visit after a new version was bundled
  useEffect(() => {
    const lastSeen = localStorage.getItem(STORAGE_KEY);
    if (lastSeen === null) {
      // First visit ever on this device (new user or fresh browser): there is
      // no "what's new" relative to them — record the version silently so the
      // changelog only appears from the NEXT release onwards.
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
      return;
    }
    if (lastSeen !== APP_VERSION) {
      // The user just loaded a new build — mark as seen and show the latest
      // changelog entry (only the most recent version; never the back catalog).
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
      setSource("localStorage");
      setNeedsReload(true);
    }
  }, []);

  // 2. Poll /version.json to detect deploys while app is open
  const checkRemoteVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.version && data.version !== APP_VERSION) {
        setSource("deploy");
        setNeedsReload(true);
      }
    } catch {
      // Network error — ignore, will retry next interval
    }
  }, []);

  useEffect(() => {
    if (IS_DEV) return; // Skip polling in dev — version.json may be stale
    const id = setInterval(checkRemoteVersion, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [checkRemoteVersion]);

  // Also check on visibility change (user switches back to tab)
  useEffect(() => {
    if (IS_DEV) return;
    function onVisible() {
      if (document.visibilityState === "visible") {
        checkRemoteVersion();
      }
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [checkRemoteVersion]);

  if (!needsReload) return null;

  // For localStorage source (fresh load with new version already loaded),
  // "Got it" is enough — the new code is already running.
  // For deploy source (new version detected while app was open), force reload.
  const isHotDeploy = source === "deploy";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="card mx-4 w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="border-b border-line-2 bg-panel-2 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft">
              <Sparkles className="h-5 w-5 text-accent" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-ink">
                {isHotDeploy ? "New version available" : "What's new in TRACE"}
              </h2>
              <p className="text-sm text-ink-3">
                {isHotDeploy
                  ? "A new version has been deployed. Please reload to continue."
                  : `You're now on v${APP_VERSION}`}
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] space-y-6 overflow-y-auto px-6 py-5">
          {APP_CHANGELOG.slice(0, 1).map((entry) => (
            <div key={entry.version}>
              <div className="mb-3 flex items-center justify-between">
                <span className="inline-flex items-center rounded-full bg-accent-soft px-3 py-1 text-xs font-bold text-accent">
                  v{entry.version}
                </span>
                <span className="text-xs text-ink-3">{entry.date}</span>
              </div>
              <ul className="space-y-2">
                {entry.changes.map((change, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-ink-2">
                    {changeIcon(change)}
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-line-2 px-6 py-4">
          {isHotDeploy ? (
            <button
              onClick={() => window.location.reload()}
              className="btn btn--accent w-full gap-2 px-4 py-2.5 text-sm font-medium"
            >
              <RefreshCw className="h-4 w-4" />
              Reload
            </button>
          ) : (
            <button
              onClick={() => setNeedsReload(false)}
              className="btn btn--accent w-full px-4 py-2.5 text-sm font-medium"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
